import { createWebAPI } from './api/web';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/toast/ToastContext';
import { ConnectivityWatcher } from './components/ConnectivityWatcher';
import { initSentry } from './utils/sentry';
import { initClientLogging } from './utils/logger';
import { registerServiceWorker, watchConnectivity } from './utils/serviceWorker';
import { applyAutoUiZoom, watchSharedUiZoom } from './utils/uiScale';
import { hydrateSettings } from './utils/settingsStore';
import { createRoot } from 'react-dom/client';
import './index.css';

initSentry();
initClientLogging();

// 宿主能力探测：某些内嵌浏览器**主动禁止下载** ——
// DSH 的浏览器视图监听 `will-download` 并 `event.preventDefault()`，
// 同时把所有权限请求设为 deny。此时 `a.click()` 不报错、也无任何提示，
// 文件不会落盘；若仍报"导出成功"，用户会去文件夹里白找。
// 这里检测到 DSH 宿主就标记为"下载不可用"，导出时会给出明确提示。
if (typeof window !== 'undefined' && '__DSH_BOOT__' in window) {
  (window as unknown as { __DISABLE_DOWNLOAD__?: boolean }).__DISABLE_DOWNLOAD__ = true;
}

const api = createWebAPI();
window.api = api;

// PWA：仅生产构建注册（开发环境注册会缓存产物，导致改动"刷不出来"）
registerServiceWorker();
watchConnectivity();

/**
 * 启动流程。
 *
 * ⚠️ 用 `bootstrap()` 而不是顶层 `await`：构建目标（chrome87 等）不支持
 * Top-level await，写了会让 vite build 直接失败（实测："Top-level await is not
 * available in the configured target environment"）。
 *
 * 里面第一件事是**灌入本地设置**（桌面端从 `settings.json` 来）：必须在渲染之前完成，
 * 因为 `loadLayout()` 这类同步读取发生在首个渲染期间，晚一步就会拿到空值。
 * 网页端没有本地服务，这一步会立刻返回。
 */
async function bootstrap(): Promise<void> {
  await hydrateSettings();

  /**
   * 整体界面缩放：**在首帧之前**算一次（避免先按 1 倍画一帧再跳一下）。
   * 之后的窗口尺寸变化由 `App.tsx` 的 `useAutoUiZoom` 跟进。
   * 网页端与桌面端共用这一份：桌面端壳里同样是本文件渲染的界面。
   */
  applyAutoUiZoom();
  // 副窗体（`?solo=`）跟随主窗口的缩放系数：applyAutoUiZoom 只在启动时算一次，
  // 主窗口之后 resize 发布新值时靠这里同步（见 utils/uiScale.ts 的 resolveWindowUiZoom）。
  watchSharedUiZoom();

  const root = createRoot(document.getElementById('root')!);
  root.render(
    <ErrorBoundary>
      {/* 统一提示层挂在最外层：任何页面的失败都能通过 useToast / notifyError 上报 */}
      <ToastProvider>
        {/* 断网/恢复与 SW 更新提示（需在 ToastProvider 内） */}
        <ConnectivityWatcher />
        <App />
      </ToastProvider>
    </ErrorBoundary>,
  );
}

void bootstrap();
